import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { API_URL } from '../config/api';
import { 
  X, Wallet, Tag, Repeat, BarChart3, Bell, RotateCcw, 
  AlertTriangle, Trash2, TrendingUp, TrendingDown, Clock,
  Calendar, ChevronRight, Sparkles, Shield, Settings, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import BalanceManagement from './BalanceManagement';
import PremiumMonthlyReport from './PremiumMonthlyReport';
import SavedReportsHistory from './SavedReportsHistory';
import NotificationSettings from './NotificationSettings';
import InitialSetupWizard from './InitialSetupWizard';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Menu items configuration
const MENU_ITEMS = [
  { id: 'setup', label: 'Configuration rapide', icon: Zap, color: 'from-amber-400 to-orange-500', desc: 'Revenus & dépenses fixes', highlight: true },
  { id: 'balance', label: 'Solde initial', icon: Wallet, color: 'from-emerald-400 to-teal-500', desc: 'Gérer le solde de départ' },
  { id: 'categories', label: 'Catégories', icon: Tag, color: 'from-blue-400 to-indigo-500', desc: 'Crédits & débits' },
  { id: 'recurring', label: 'Récurrentes', icon: Repeat, color: 'from-violet-400 to-purple-500', desc: 'Opérations automatiques' },
  { id: 'reports', label: 'Bilans', icon: BarChart3, color: 'from-cyan-400 to-blue-500', desc: 'Rapports mensuels' },
  { id: 'notifications', label: 'Notifications', icon: Bell, color: 'from-amber-400 to-orange-500', desc: 'Alertes & rappels' },
];

const SettingsModal = ({ open, onOpenChange, calendar, onSettingsChanged, onLogout, user }) => {
  const [categories, setCategories] = useState([]);
  const [recurringOps, setRecurringOps] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);

  useEffect(() => {
    if (open && calendar) {
      loadCategories();
      loadRecurringOperations();
    }
  }, [open, calendar]);

  useEffect(() => {
    if (!open) {
      setActiveSection(null);
    }
  }, [open]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(
        `${API}/categories?calendar_id=${calendar.id}`,
        { withCredentials: true }
      );
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadRecurringOperations = async () => {
    try {
      const response = await axios.get(
        `${API}/recurring-operations?calendar_id=${calendar.id}`,
        { withCredentials: true }
      );
      setRecurringOps(response.data);
    } catch (error) {
      console.error('Error loading recurring operations:', error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/categories/${categoryId}`, { withCredentials: true });
      toast.success('Catégorie supprimée');
      loadCategories();
      onSettingsChanged();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteRecurring = async (recurringId) => {
    try {
      await axios.delete(`${API}/recurring-operations/${recurringId}`, { withCredentials: true });
      toast.success('Opération récurrente supprimée');
      loadRecurringOperations();
      onSettingsChanged();
    } catch (error) {
      console.error('Error deleting recurring operation:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMenuClick = (menuId) => {
    if (menuId === 'setup') {
      setShowSetupWizard(true);
    } else if (menuId === 'reports') {
      setActiveSection('reports');
    } else if (menuId === 'notifications') {
      setShowNotifications(true);
    } else {
      setActiveSection(menuId);
    }
  };

  if (!open) return null;

  const creditCategories = categories.filter(c => c.type === 'credit');
  const debitCategories = categories.filter(c => c.type === 'debit');

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
          
          {/* Content */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="relative w-full sm:max-w-lg max-h-[95vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-700 to-gray-800 p-4 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"
                  >
                    <Settings className="h-5 w-5 text-white" />
                  </motion.div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Paramètres</h2>
                    <p className="text-xs text-white/70">{calendar?.name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => onOpenChange(false)} 
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(95vh - 120px)' }}>
              <AnimatePresence mode="wait">
                {!activeSection ? (
                  /* Main Menu */
                  <motion.div
                    key="menu"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="p-4 space-y-3"
                  >
                    {MENU_ITEMS.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => handleMenuClick(item.id)}
                        className="w-full p-4 rounded-2xl bg-white border-2 border-gray-100 hover:border-gray-200 hover:shadow-md transition-all flex items-center gap-4"
                        data-testid={`settings-${item.id}`}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                          <item.icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="font-semibold text-gray-900">{item.label}</h3>
                          <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </button>
                    ))}

                    {/* Reset Usine - Visible pour tous les utilisateurs (Phase Bêta) */}
                    <a
                      href="/reset"
                      className="w-full p-4 rounded-2xl bg-orange-50 border-2 border-orange-200 hover:border-orange-300 hover:shadow-md transition-all flex items-center gap-4"
                      data-testid="settings-reset"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                        <RotateCcw className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-orange-800">Reset Usine</h3>
                        <p className="text-sm text-orange-600">Réinitialiser toutes les données</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-orange-400" />
                    </a>

                    {/* Footer quote */}
                    <div className="text-center pt-4 pb-2">
                      <p className="text-sm text-gray-400 italic">
                        🌴 "La maîtrise de son budget, c'est la liberté"
                      </p>
                    </div>
                  </motion.div>
                ) : activeSection === 'balance' ? (
                  /* Balance Section */
                  <motion.div
                    key="balance"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="p-4"
                  >
                    <button 
                      onClick={() => setActiveSection(null)}
                      className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
                    >
                      <ChevronRight className="h-4 w-4 rotate-180" />
                      <span className="text-sm">Retour</span>
                    </button>
                    <BalanceManagement calendar={calendar} onBalanceChanged={onSettingsChanged} />
                  </motion.div>
                ) : activeSection === 'categories' ? (
                  /* Categories Section */
                  <motion.div
                    key="categories"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="p-4 space-y-4"
                  >
                    <button 
                      onClick={() => setActiveSection(null)}
                      className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
                    >
                      <ChevronRight className="h-4 w-4 rotate-180" />
                      <span className="text-sm">Retour</span>
                    </button>

                    {/* Credit Categories */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-200">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                        <h3 className="font-semibold text-emerald-800">Catégories de crédit</h3>
                      </div>
                      {creditCategories.length === 0 ? (
                        <p className="text-sm text-emerald-600/70 py-4 text-center">
                          Aucune catégorie de crédit personnalisée
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {creditCategories.map(cat => (
                            <div key={cat.id} className="flex justify-between items-center p-3 bg-white rounded-xl border border-emerald-100">
                              <span className="font-medium text-gray-900">{cat.name}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteCategory(cat.id)}
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Debit Categories */}
                    <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-4 border border-rose-200">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="h-5 w-5 text-rose-600" />
                        <h3 className="font-semibold text-rose-800">Catégories de débit</h3>
                      </div>
                      {debitCategories.length === 0 ? (
                        <p className="text-sm text-rose-600/70 py-4 text-center">
                          Aucune catégorie de débit personnalisée
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {debitCategories.map(cat => (
                            <div key={cat.id} className="flex justify-between items-center p-3 bg-white rounded-xl border border-rose-100">
                              <span className="font-medium text-gray-900">{cat.name}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteCategory(cat.id)}
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ) : activeSection === 'recurring' ? (
                  /* Recurring Operations Section */
                  <motion.div
                    key="recurring"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="p-4 space-y-4"
                  >
                    <button 
                      onClick={() => setActiveSection(null)}
                      className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
                    >
                      <ChevronRight className="h-4 w-4 rotate-180" />
                      <span className="text-sm">Retour</span>
                    </button>

                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-4 border border-violet-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Repeat className="h-5 w-5 text-violet-600" />
                        <h3 className="font-semibold text-violet-800">Opérations récurrentes</h3>
                      </div>
                      
                      {recurringOps.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Clock className="h-7 w-7 text-violet-500" />
                          </div>
                          <p className="text-sm text-violet-600">Aucune opération récurrente</p>
                          <p className="text-xs text-violet-500 mt-1">Les prélèvements automatiques apparaîtront ici</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {recurringOps.map(op => (
                            <div key={op.id} className="p-4 bg-white rounded-xl border border-violet-100">
                              <div className="flex justify-between items-start gap-3">
                                <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                    op.type === 'credit' ? 'bg-emerald-100' : 'bg-rose-100'
                                  }`}>
                                    {op.type === 'credit' 
                                      ? <TrendingUp className="h-5 w-5 text-emerald-600" />
                                      : <TrendingDown className="h-5 w-5 text-rose-600" />
                                    }
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-900">{op.name}</h4>
                                    <p className={`text-lg font-bold ${
                                      op.type === 'credit' ? 'text-emerald-600' : 'text-rose-600'
                                    }`}>
                                      {op.type === 'credit' ? '+' : '-'}{op.amount} {calendar.currency}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                        📁 {op.category}
                                      </span>
                                      <span className="text-xs bg-violet-100 text-violet-600 px-2 py-0.5 rounded-full">
                                        📅 Jour {op.day_of_month}
                                      </span>
                                      {op.frequency_type && op.frequency_type !== 'mensuel' && (
                                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                                          🔄 {op.frequency_type}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteRecurring(op.id)}
                                  className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ) : activeSection === 'reports' ? (
                  /* Reports Section */
                  <motion.div
                    key="reports"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="p-4 space-y-4"
                  >
                    <button 
                      onClick={() => setActiveSection(null)}
                      className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
                    >
                      <ChevronRight className="h-4 w-4 rotate-180" />
                      <span className="text-sm">Retour</span>
                    </button>

                    {/* Premium Report Card */}
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-6 text-white cursor-pointer"
                      onClick={() => setShowReport(true)}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                          <BarChart3 className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">Bilan mensuel premium</h3>
                          <p className="text-sm text-white/80">Analyse complète du mois</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-sm">Comparaison, projection, message coach</span>
                      </div>
                    </motion.div>

                    {/* History Card */}
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl p-6 text-white cursor-pointer"
                      onClick={() => setShowHistory(true)}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                          <Calendar className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">Historique des bilans</h3>
                          <p className="text-sm text-white/80">Consultez vos rapports passés</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        <span className="text-sm">Bilans sauvegardés disponibles</span>
                      </div>
                    </motion.div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Premium Monthly Report Modal */}
      <PremiumMonthlyReport
        open={showReport}
        onClose={() => setShowReport(false)}
        calendarId={calendar?.id}
        currency={calendar?.currency}
      />

      {/* Saved Reports History Modal */}
      <SavedReportsHistory
        open={showHistory}
        onClose={() => setShowHistory(false)}
        calendarId={calendar?.id}
      />

      {/* Notification Settings Modal */}
      <NotificationSettings
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        user={user}
      />

      {/* Initial Setup Wizard */}
      <InitialSetupWizard
        open={showSetupWizard}
        onClose={() => setShowSetupWizard(false)}
        calendarId={calendar?.id}
        onComplete={() => {
          setShowSetupWizard(false);
          onSettingsChanged();
          toast.success('Configuration terminée ! Ton budget est prêt.');
        }}
      />
    </>
  );
};

export default SettingsModal;
